import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Menubar, MenubarProps } from 'primereact/menubar';
import { FilterMatchMode } from 'primereact/api';
import {
  DataTable,
  DataTableFilterMeta,
  DataTableSelectionMultipleChangeEvent,
} from 'primereact/datatable';
import CheckIcon from '@mui/icons-material/CheckOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import { Paper } from '@mui/material';
import { useStores } from 'stores/hooks/hooks';
import { DialogCloseAction } from 'models/shared';
import { Action } from 'models/restTypes';
import { ActionType } from 'models/actions';
import api from 'Api/ApiMethods';
import ConfirmDialog from 'components/UI/ConfirmDialog';
import { Column } from 'components/shared/DataTable/DataTable';
import Grid from 'components/UI/Grid';
import Loading from 'components/UI/Loading/Loading';
import { HIGHLIGHTED_ROW_CLASS_NAME } from 'core/constants';

const Container = styled(Paper)`
  padding: 1rem;
  padding-top: 0.5rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: transparent;
`;

const DataTableContainer = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const StyledBlockContainer = styled(Grid)`
  height: 100%;
  width: 100%;
  overflow: hidden;

  .${HIGHLIGHTED_ROW_CLASS_NAME} {
    td {
      background-color: var(--highlight-bg);
    }
  }
`;

const tableFilters: DataTableFilterMeta = {
  name: { value: '', matchMode: FilterMatchMode.CONTAINS },
  serial: { value: '', matchMode: FilterMatchMode.CONTAINS },
  mode: { value: '', matchMode: FilterMatchMode.CONTAINS },
  systemName: { value: '', matchMode: FilterMatchMode.CONTAINS },
};

type PlotActionRow = (Action | ActionType) & { systemName?: string };
type ConfirmActionType = 'Add' | 'Remove';

const PlotActionsTable: FC = observer(() => {
  const { t } = useTranslation('grower');
  const { resellersStore, snackBarStore } = useStores();

  const selectedPlotId = resellersStore.selectedRowsPlots[0]?.id;
  const growerId = resellersStore.selectedGrowerId;

  const [plotActions, setPlotActions] = useState<Action[]>([]);
  const [growerActions, setGrowerActions] = useState<ActionType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmVisible, setConfirmVisible] = useState(false);
  const [confirmActionType, setConfirmActionType] =
    useState<ConfirmActionType>();

  const [selectedRows, setSelectedRows] = useState<PlotActionRow[]>([]);

  const isRemoveActionType = confirmActionType === 'Remove';

  const fetchActions = useCallback(async () => {
    if (!growerId || !selectedPlotId) {
      return;
    }

    setIsLoading(true);
    try {
      const [plotRes, growerRes] = await Promise.all([
        api.getGrowerActions(growerId, selectedPlotId),
        api.getActionsExcludingPlot(growerId, selectedPlotId),
      ]);

      setPlotActions(plotRes?.actions ?? []);
      setGrowerActions(growerRes?.actions ?? []);
    } finally {
      setIsLoading(false);
    }
  }, [growerId, selectedPlotId]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const plotActionIds = useMemo(
    () => new Set(plotActions.map((a) => a.id)),
    [plotActions],
  );

  const tableRows: PlotActionRow[] = useMemo(() => {
    const attached = plotActions.map((a) => ({ ...a, systemName: undefined }));
    const available = growerActions.map((a) => ({
      ...a,
      systemName: undefined,
    }));

    return [...attached, ...available];
  }, [plotActions, growerActions]);

  const getAttachedColumnBody = useCallback(
    (row: PlotActionRow) =>
      plotActionIds.has(row.id) ? <CheckIcon /> : <CloseIcon />,
    [plotActionIds],
  );

  const onSelectionChange = useCallback(
    (event: DataTableSelectionMultipleChangeEvent<PlotActionRow[]>) => {
      const attachmentValues = event.value.map((row) =>
        plotActionIds.has(row.id),
      );

      const isValidSelection = attachmentValues.every(
        (v) => v === attachmentValues[0],
      );

      if (isValidSelection) {
        setSelectedRows(event.value);
      } else {
        snackBarStore.showToast({
          detail: t('invalid_plot_sensor_selection_error'),
        });
      }
    },
    [plotActionIds, snackBarStore, t],
  );

  const attachSelectedActions = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all(
        selectedRows.map((row) =>
          api.putPlotActionAttachment(growerId, selectedPlotId, row.id),
        ),
      );

      await fetchActions();
      setSelectedRows([]);
      setConfirmVisible(false);
    } catch {
      setIsLoading(false);
    }
  }, [growerId, selectedPlotId, selectedRows, fetchActions]);

  const detachSelectedActions = useCallback(async () => {
    setIsLoading(true);
    try {
      await Promise.all(
        selectedRows.map((row) =>
          api.deletePlotActionAttachment(growerId, selectedPlotId, row.id),
        ),
      );

      await fetchActions();
      setSelectedRows([]);
      setConfirmVisible(false);
    } catch {
      setIsLoading(false);
    }
  }, [growerId, selectedPlotId, selectedRows, fetchActions]);

  const handleConfirmSubmit = useCallback(
    (action: DialogCloseAction) => {
      if (action !== DialogCloseAction.Confirmed) {
        setConfirmVisible(false);
        return;
      }

      if (isRemoveActionType) {
        detachSelectedActions();
      } else {
        attachSelectedActions();
      }
    },
    [isRemoveActionType, attachSelectedActions, detachSelectedActions],
  );

  const showConfirmDialog = useCallback((action: ConfirmActionType) => {
    setConfirmActionType(action);
    setConfirmVisible(true);
  }, []);

  const isFirstSelectedAttached =
    selectedRows.length > 0 && plotActionIds.has(selectedRows[0].id);

  console.log('selectedRows', selectedRows)

  const menuBarActions: MenubarProps['model'] = useMemo(
    () => [
      {
        label: i18next.t('general:add'),
        icon: 'pi pi-fw pi-plus',
        disabled: !selectedRows.length || isFirstSelectedAttached,
        command: () => showConfirmDialog('Add'),
      },
      {
        label: i18next.t('general:remove'),
        icon: 'pi pi-fw pi-trash',
        disabled: !selectedRows.length || !isFirstSelectedAttached,
        command: () => showConfirmDialog('Remove'),
      },
    ],
    [showConfirmDialog, selectedRows.length, isFirstSelectedAttached],
  );

  if (isLoading && !tableRows.length) {
    return <Loading />;
  }

  return (
    <StyledBlockContainer>
      <Container>
        <Menubar model={menuBarActions} />
        <DataTableContainer>
          <DataTable
            selectionMode="checkbox"
            id="PlotActionsTable"
            dataKey="id"
            showGridlines
            resizableColumns
            value={tableRows}
            scrollable
            scrollHeight="100%"
            width="100%"
            emptyMessage={t('empty_message')}
            className="height-percent-100 width-percent-100"
            stripedRows
            filters={tableFilters}
            filterDisplay="row"
            selection={selectedRows}
            onSelectionChange={onSelectionChange}
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
            <Column header={t('attached')} body={getAttachedColumnBody} />
            <Column field="name" header={t('name')} sortable filter />
            <Column field="serial" header={t('serial')} sortable filter />
            <Column field="mode" header={t('mode')} sortable filter />
            <Column field="systemName" header={t('system')} sortable filter />
          </DataTable>
        </DataTableContainer>
        <ConfirmDialog
          backText={i18next.t('general:cancel')}
          buttonText={
            isRemoveActionType
              ? i18next.t('general:remove')
              : i18next.t('general:add')
          }
          isOpen={isConfirmVisible}
          title={
            isRemoveActionType
              ? i18next.t('dialog:confirm_dialog_title', { action: 'Remove' })
              : i18next.t('dialog:confirm_dialog_title', { action: 'Add' })
          }
          message={
            isRemoveActionType
              ? i18next.t('dialog:confirm_dialog_action_message', {
                action: 'Remove',
                entity: 'actions',
              })
              : i18next.t('dialog:confirm_dialog_action_message', {
                action: 'Add',
                entity: 'actions',
              })
          }
          onClose={handleConfirmSubmit}
          loading={isLoading}
          disabled={isLoading}
        />
      </Container>
    </StyledBlockContainer>
  );
});

export default PlotActionsTable;
